import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IFile, IFileListing } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AddFile from './add-file';

const FilesListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [allData, setAllData] = useState<IFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddFile, setOpenAddFile] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<IFile | null>(null);

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this file?',
      onSubmit: async () => {
        const result = await DataService.post('api/files/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'File activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate file', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this file?',
      onSubmit: async () => {
        const result = await DataService.post('api/files/deactivate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'File deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate file', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deleteFile = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this file? This action cannot be undone.',
      onSubmit: async () => {
        const result = await DataService.delete('api/files/delete/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'File deleted successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to delete file', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editFile = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/files/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const file: IFile = res.data;
        setSelectedFile(file);
        setOpenAddFile(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch file details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading file details', type: 'error' }));
    }
  }, [dispatch]);

  const downloadFile = useCallback((id: string) => {
    const file = allData.find(f => f.id === id);
    if (file && file.file) {
      window.open(file.file, '_blank');
    }
  }, [allData]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'title_En', label: 'Title (EN)', type: EnhancedTableColumnType.TEXT },
    { id: 'title_Ar', label: 'Title (AR)', type: EnhancedTableColumnType.TEXT },
    { id: 'title_Fr', label: 'Title (FR)', type: EnhancedTableColumnType.TEXT },
    { id: 'order', label: 'Order', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: downloadFile,
          icon: <DownloadIcon fontSize="small" />,
          label: 'Download File',
          color: 'primary'
        },
        {
          onClick: editFile,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit File'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate File',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate File',
          hidden: (r) => r.active === false
        },
        {
          onClick: deleteFile,
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete File',
          color: 'error'
        }
      ]
    }
  ], [activate, deactivate, deleteFile, editFile, downloadFile]);

  const getTableData = useCallback((): Record<string, IFileListing> => {
    const result: Record<string, IFileListing> = {};

    allData.forEach((file) => {
      result[file.id] = {
        id: file.id,
        title_En: file.title.en,
        title_Ar: file.title.ar,
        title_Fr: file.title.fr,
        order: file.order,
        active: file.active,
        status: file.active ? 'Active' : 'Inactive',
        file: file.file
      };
    });

    return result;
  }, [allData]);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/files/all');
      if (response.ok) {
        const res = await response.json();
        const list: IFile[] = res.data;
        setAllData(list);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch files', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading files', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddFile(false);
    setSelectedFile(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: selectedFile ? 'File updated successfully!' : 'File added successfully!',
      type: 'success'
    }));
  }, [dispatch, selectedFile]);

  const handleClose = useCallback(() => {
    setOpenAddFile(false);
    setSelectedFile(null);
  }, []);

  const actions: ITableMenuAction[] = [];

  if (loading) {
    return (
      <Box>
        <Helmet>
          <title>Files List</title>
        </Helmet>
        <MainPageTitle title={'Files List'} />
        <Loader />
      </Box>
    );
  }

  const currentData = getTableData();

  return (
    <div>
      <Helmet>
        <title>Files List</title>
      </Helmet>
      <MainPageTitle
        title={'Files List'}
        action={{
          title: 'Add File',
          onClick: () => {
            setOpenAddFile(true);
          }
        }}
      />

      <EnhancedTable
        title="Files"
        columns={headers}
        data={currentData}
        actions={actions}
      />
      {openAddFile && (
        <AddFile
          onSuccess={handleAddSuccess}
          onClose={handleClose}
          file={selectedFile}
        />
      )}
    </div>
  );
};

export default FilesListing;
